(()=>{"use strict";
const ROOT='[data-wsnz-khome]';

function init(root=document){
  root.querySelectorAll(ROOT).forEach(sec=>{
    if(sec.dataset.ready)return;
    sec.dataset.ready='1';
    reveal(sec);
    bindKingdomUISounds(sec);
    initKingdomJourney(sec);
    waitForDatabase(sec);
  });
}

function reveal(sec){
  const nodes=[...sec.querySelectorAll('[data-khome-reveal]')];
  if(!('IntersectionObserver' in window)){
    nodes.forEach(node=>node.classList.add('is-visible'));
    return;
  }
  const observer=new IntersectionObserver(entries=>{
    entries.forEach(entry=>{
      if(!entry.isIntersecting)return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  },{rootMargin:'0px 0px -10% 0px',threshold:.12});
  nodes.forEach((node,index)=>{
    node.style.transitionDelay=Math.min(index*.035,.24)+'s';
    observer.observe(node);
  });
}

function bindKingdomUISounds(sec){
  const manager=window.WSNZ?.audio;
  if(!manager||sec.dataset.uiAudioMounted)return;
  sec.dataset.uiAudioMounted='1';
  manager.mountUISounds(sec,{
    hoverSelector:'.wsnz-khome__button, .wsnz-khome__journey-link, .wsnz-kjourney__finish a, .wsnz-kjourney__controls button',
    effects:{
      hover:{id:'button-hover',src:sec.dataset.soundHover,volume:.18,poolSize:2},
      click:{id:'button-click',src:sec.dataset.soundClick,volume:.42,poolSize:3}
    },
    clickRules:[{
      selector:'.wsnz-khome__button, .wsnz-khome__journey-link, .wsnz-kjourney__finish a, .wsnz-kjourney__controls button',
      effect:'click'
    }]
  });
}

function waitForDatabase(sec,attempt=0){
  if(window.WSNZ&&WSNZ.getDatabase&&WSNZ.getDiscoveries&&WSNZ.getUnlockedIds){
    try{
      WSNZ.getDatabase();
      renderJourney(sec);
      return;
    }catch(error){
      if(!sec.dataset.databaseLoadStarted&&typeof WSNZ.load==='function'){
        sec.dataset.databaseLoadStarted='1';
        Promise.resolve(WSNZ.load(window.WSNZ_DATABASE_URL||window.WSNZ_DATABASE_V3_URL))
          .then(()=>renderJourney(sec))
          .catch(loadError=>console.error('[WSNZ Kingdom Home] Database load failed:',loadError));
        return;
      }
    }
  }
  if(attempt<120){
    setTimeout(()=>waitForDatabase(sec,attempt+1),100);
  }else{
    console.error('[WSNZ Kingdom Home] WSNZ database runtime did not become ready.');
  }
}

function renderJourney(sec){
  const slug=normalise(sec.dataset.kingdom);
  const discoveries=WSNZ.getDiscoveries().filter(item=>
    String(item.status||'active').toLowerCase()==='active'&&
    normalise(item.kingdomId||item.kingdom)===slug
  );
  const unlockedIds=new Set(getUnlockedIdsCompat());
  const found=discoveries.filter(item=>
    unlockedIds.has(String(item.id||'').toUpperCase())
  ).length;
  const total=discoveries.length;
  const complete=total?Math.round(found/total*100):0;

  setText(sec,'[data-k-journey-found]',found);
  setText(sec,'[data-k-journey-total]',total);
  setText(sec,'[data-k-journey-percent]',complete+'%');
}

function getUnlockedIdsCompat(){
  const ids=new Set();
  const add=value=>{
    if(value&&typeof value==='object'){
      value=value.id||value.discoveryId||value.code||value.storageKey||'';
    }
    const id=String(value||'').trim().toUpperCase();
    if(id)ids.add(id);
  };

  try{
    const runtimeIds=WSNZ.getUnlockedIds?.();
    if(Array.isArray(runtimeIds))runtimeIds.forEach(add);
  }catch{}

  ['wsnzUnlockedDiscoveries','wsNzUnlockedDiscoveries'].forEach(key=>{
    try{
      const parsed=JSON.parse(localStorage.getItem(key)||'[]');
      if(Array.isArray(parsed))parsed.forEach(add);
    }catch{}
  });

  return [...ids];
}

function initKingdomJourney(sec){
  const root=sec.querySelector('[data-kjourney]');
  const viewport=root?.querySelector('[data-kjourney-viewport]');
  const track=root?.querySelector('[data-kjourney-track]');
  if(!root||!viewport||!track||root.dataset.ready)return;

  root.dataset.ready='1';

  const explorer=root.querySelector('[data-kjourney-explorer]');
  const progress=root.querySelector('[data-kjourney-progress]');
  const status=root.querySelector('[data-kjourney-status]');
  const prev=root.querySelector('[data-kjourney-prev]');
  const next=root.querySelector('[data-kjourney-next]');
  const texts=[...root.querySelectorAll('[data-kjourney-text]')];
  const stops=[.08,.34,.60,.82,1];

  let dragging=false,startX=0,startLeft=0,raf=0;

  function maxScroll(){
    return Math.max(1,viewport.scrollWidth-viewport.clientWidth);
  }

  function update(){
    raf=0;
    const max=maxScroll();
    const p=Math.min(1,Math.max(0,viewport.scrollLeft/max));

    root.style.setProperty('--journey-progress',p.toFixed(4));

    if(progress){
      progress.style.transform=`scaleX(${p})`;
    }

    let active=0;
    const centers=[.10,.34,.58,.80];
    let best=99;

    centers.forEach((c,i)=>{
      const d=Math.abs(p-c);
      if(d<best){
        best=d;
        active=i;
      }
    });

    texts.forEach((node,i)=>node.classList.toggle('is-current',i===active));

    if(status){
      status.textContent=`0${active+1} / 04`;
    }

    if(explorer){
      const viewportWidth=viewport.clientWidth;
      const explorerWidth=explorer.getBoundingClientRect().width||0;

      const startScreenX=Math.max(0,viewportWidth*.82-explorerWidth*.5);
      const endScreenX=Math.max(0,viewportWidth*.18-explorerWidth*.5);

      const screenX=startScreenX+((endScreenX-startScreenX)*p);
      const trackX=viewport.scrollLeft+screenX;

      explorer.style.setProperty('--explorer-x',`${trackX}px`);
    }

    root.classList.toggle('is-finished',p>.92);
  }

  function queue(){
    if(!raf)raf=requestAnimationFrame(update);
  }

  function go(index){
    const p=stops[Math.max(0,Math.min(stops.length-1,index))];
    viewport.scrollTo({left:maxScroll()*p,behavior:'smooth'});
  }

  function currentStop(){
    const p=viewport.scrollLeft/maxScroll();
    let idx=0,best=99;
    stops.forEach((s,i)=>{
      const d=Math.abs(p-s);
      if(d<best){
        best=d;
        idx=i;
      }
    });
    return idx;
  }

  prev?.addEventListener('click',()=>go(currentStop()-1));
  next?.addEventListener('click',()=>go(currentStop()+1));

  viewport.addEventListener('scroll',queue,{passive:true});

  viewport.addEventListener('wheel',e=>{
    const max=maxScroll();
    if(max<=1)return;

    const delta=Math.abs(e.deltaX)>Math.abs(e.deltaY)?e.deltaX:e.deltaY;
    if(!delta)return;

    const atStart=viewport.scrollLeft<=1;
    const atEnd=viewport.scrollLeft>=max-1;

    if((atStart&&delta<0)||(atEnd&&delta>0)){
      return;
    }

    e.preventDefault();
    viewport.scrollLeft+=delta*1.15;
  },{passive:false});

  viewport.addEventListener('keydown',e=>{
    if(e.key==='ArrowRight'){
      e.preventDefault();
      go(currentStop()+1);
    }
    if(e.key==='ArrowLeft'){
      e.preventDefault();
      go(currentStop()-1);
    }
  });

  viewport.addEventListener('pointerdown',e=>{

  /*
   * Links and buttons must keep their normal click behaviour.
   * Do not start panorama dragging from an interactive element.
   */
  if(e.target.closest('a, button')){
    return;
  }

  if(e.pointerType==='mouse'){
    dragging=true;
    startX=e.clientX;
    startLeft=viewport.scrollLeft;

    viewport.setPointerCapture(e.pointerId);

    root.classList.add('is-dragging');
  }
});
   

  viewport.addEventListener('pointermove',e=>{
    if(!dragging)return;
    viewport.scrollLeft=startLeft-(e.clientX-startX)*1.35;
  });

  const endDrag=()=>{
    dragging=false;
    root.classList.remove('is-dragging');
  };

  viewport.addEventListener('pointerup',endDrag);
  viewport.addEventListener('pointercancel',endDrag);

  new ResizeObserver(queue).observe(viewport);
  queue();
}

function normalise(value){
  return String(value||'')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-z0-9]+/g,'-')
    .replace(/^-|-$/g,'');
}

function setText(sec,selector,value){
  const node=sec.querySelector(selector);
  if(node)node.textContent=value;
}

document.addEventListener('DOMContentLoaded',()=>init());
document.addEventListener('shopify:section:load',event=>init(event.target));
})();